import { Injectable, OnDestroy, Signal, resource } from '@angular/core';
import { AnalyticsInfo, AnalyticsRangeContext } from '@services/analytics-services/core'; // Import AnalyticsInfo and AnalyticsRangeContext
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { DynamicQueryService } from '@services/analytics-services/core/dynamic-query.service';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import {
  CustomerAnalyticsService,
  CustomerStats,
  TopCustomerStats,
} from '@services/analytics-services/customers';
import { SubsequentPurchasesStats } from '@services/analytics-services/customers/subsequent-purchases-stats';
import { TopCustomerList } from '@services/analytics-services/customers/dashboard-customer-table-header.constant';
import { SubsequentPurchasesList } from '@services/analytics-services/customers/dashboard-subsequent-purchase-table-header.constant';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { DateRangePresetType, ProcessingState } from '@services/core-services/models';
import {
  ClientSideSortingService,
  SortConfig,
} from '@services/core-services/client-side-sorting.service';
import { AutoTableSortingHelper } from '@services/core-services/auto-table-sorting.mixin';
import { BehaviorSubject, Observable, Subject, catchError, of, tap, firstValueFrom } from 'rxjs';

@Injectable()
export class DashboardCustomersStore implements OnDestroy {
  private topCustomer = new BehaviorSubject<TopCustomerStats[] | undefined>(undefined);
  private originalTopCustomer = new BehaviorSubject<TopCustomerStats[] | undefined>(undefined);
  private subsequentPurchases = new BehaviorSubject<SubsequentPurchasesStats[] | undefined>(
    undefined
  );
  private originalSubsequentPurchases = new BehaviorSubject<SubsequentPurchasesStats[] | undefined>(
    undefined
  );

  private topCustomerLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private subsequentPurchasesLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);

  private topCustomerErrorMessage = new BehaviorSubject<string>('');
  private subsequentPurchasesErrorMessage = new BehaviorSubject<string>('');

  // Subject for unsubscribing
  private destroy$ = new Subject<void>();

  // Client-side sorting handlers
  private topCustomersSortHandler: ((sortConfig: SortConfig) => void) | undefined;
  private subsequentPurchasesSortHandler: ((sortConfig: SortConfig) => void) | undefined;
  private CustomersSortConfig: SortConfig = {};

  constructor(
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly customerAnalyticsService: CustomerAnalyticsService,
    private readonly clientSideSortingService: ClientSideSortingService
  ) {
    // Initialize client-side sorting handler for top customers
    this.topCustomersSortHandler = AutoTableSortingHelper.createSortHandler(
      this.clientSideSortingService,
      this.topCustomer,
      this.originalTopCustomer,
      TopCustomerList,
      {}
    );

    // Initialize client-side sorting handler for subsequent purchases
    this.subsequentPurchasesSortHandler = AutoTableSortingHelper.createSortHandler(
      this.clientSideSortingService,
      this.subsequentPurchases,
      this.originalSubsequentPurchases,
      SubsequentPurchasesList,
      {}
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTopCustomers(): Observable<TopCustomerStats[] | undefined> {
    return this.topCustomer.asObservable();
  }

  getSubsequentPurchases(): Observable<SubsequentPurchasesStats[] | undefined> {
    return this.subsequentPurchases.asObservable();
  }

  getTopCustomerLoader(): Observable<ProcessingState> {
    return this.topCustomerLoader.asObservable();
  }

  getsubsequentPurchasesLoader(): Observable<ProcessingState> {
    return this.subsequentPurchasesLoader.asObservable();
  }

  getTopCustomerErrorMessage(): Observable<string> {
    return this.topCustomerErrorMessage.asObservable();
  }

  getSubsequentPurchasesErrorMessage(): Observable<string> {
    return this.subsequentPurchasesErrorMessage.asObservable();
  }

  /**
   * Sort top customers client-side without making a new API call
   */
  sortTopCustomers(sortConfig: SortConfig): void {
    this.CustomersSortConfig = sortConfig;
    if (this.topCustomersSortHandler) {
      this.topCustomersSortHandler(sortConfig);
    }
  }

  /**
   * Sort subsequent purchases client-side without making a new API call
   */
  sortSubsequentPurchases(sortConfig: SortConfig): void {
    this.CustomersSortConfig = sortConfig;
    if (this.subsequentPurchasesSortHandler) {
      this.subsequentPurchasesSortHandler(sortConfig);
    }
  }

  /**
   * Check if there's an active sort configuration for top customers
   */
  private hasActiveSort(): boolean {
    return Object.values(this.CustomersSortConfig).some(direction => direction !== 'default');
  }

  loadTopCustomer(context: AnalyticsRangeContext): Observable<TopCustomerStats[]> {
    this.topCustomerLoader.next(ProcessingState.Loading);
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.TOP_CUSTOMERS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((customers: TopCustomerStats[]) => {
          this.topCustomerLoader.next(ProcessingState.Success);
          this.originalTopCustomer.next([...customers]); // Store original data
          this.topCustomer.next(customers);
          // Reapply current sort to new data if there was a previous sort
          if (this.hasActiveSort() && this.topCustomersSortHandler) {
            this.topCustomersSortHandler(this.CustomersSortConfig);
          }
        }),
        catchError(error => {
          this.topCustomerLoader.next(ProcessingState.Error);
          this.handleError('Error loading top customers', error, this.topCustomerErrorMessage);
          this.topCustomer.next(undefined);
          this.originalTopCustomer.next(undefined);
          return of([] as TopCustomerStats[]);
        })
      );
  }

  loadSubsequentPurchases(context: AnalyticsRangeContext): Observable<SubsequentPurchasesStats[]> {
    this.subsequentPurchasesLoader.next(ProcessingState.Loading);
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.AVERAGE_TIME_BETWEEN_SUBSEQUENT_PURCHASES,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((purchases: SubsequentPurchasesStats[]) => {
          this.subsequentPurchasesLoader.next(ProcessingState.Success);
          this.originalSubsequentPurchases.next([...purchases]); // Store original data
          this.subsequentPurchases.next(purchases);
          // Reapply current sort to new data if there was a previous sort
          if (this.hasActiveSort() && this.subsequentPurchasesSortHandler) {
            this.subsequentPurchasesSortHandler(this.CustomersSortConfig);
          }
        }),
        catchError(error => {
          this.subsequentPurchasesLoader.next(ProcessingState.Error);
          this.handleError(
            'Error loading subsequent purchases',
            error,
            this.subsequentPurchasesErrorMessage
          );
          this.subsequentPurchases.next(undefined);
          this.originalSubsequentPurchases.next(undefined);
          return of([] as SubsequentPurchasesStats[]);
        })
      );
  }

  /**
   * Creates a resource for loading customer analytics data
   */
  public createCustomerAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: ({ params }): Promise<AnalyticsInfo<CustomerStats>> => {
        const { range, compare, interval, preset } = params;
        return firstValueFrom(
          this.customerAnalyticsService.getAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
          })
        );
      },
    });
  }

  // --- Error Handling ---
  private handleError(message: string, error: any, errorSubject: BehaviorSubject<string>): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    errorSubject.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }
}
